import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toSafeFixed } from '../utils/numberUtils';

interface AgingData {
  invoice_number: string;
  date: string;
  reference: string;
  amount: number;
  balance: number;
  days: number;
  current: number;
  days_31_60: number;
  days_61_90: number;
  over_90: number;
}

const ARAgingPage: React.FC = () => {
  const [data, setData] = useState<AgingData[]>([]);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [totals, setTotals] = useState({ current: 0, days_31_60: 0, days_61_90: 0, over_90: 0, total: 0 });

  const fetchData = async () => {
    try {
      const response = await axios.get(`/api/v1/account/reports/ar-aging?as_of_date=${asOfDate}`);
      setData(response.data.data || []);
      setTotals(response.data.totals || { current: 0, days_31_60: 0, days_61_90: 0, over_90: 0, total: 0 });
    } catch (error) {
      console.error('Error fetching AR aging:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">AR Aging Report</h1>
      
      <div className="mb-4 flex gap-4">
        <input
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          className="border rounded px-3 py-2"
        />
        <button onClick={fetchData} className="bg-blue-500 text-white px-4 py-2 rounded">
          Refresh
        </button>
      </div>

      <div className="mb-4 bg-gray-100 p-4 rounded">
        <div className="flex gap-6">
          <div><strong>Total:</strong> {toSafeFixed(totals.total, 2)}</div>
          <div><strong>Current:</strong> {toSafeFixed(totals.current, 2)}</div>
          <div><strong>31-60:</strong> {toSafeFixed(totals.days_31_60, 2)}</div>
          <div><strong>61-90:</strong> {toSafeFixed(totals.days_61_90, 2)}</div>
          <div><strong>Over 90:</strong> {toSafeFixed(totals.over_90, 2)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2 border">Invoice #</th>
              <th className="px-4 py-2 border">Date</th>
              <th className="px-4 py-2 border">Reference</th>
              <th className="px-4 py-2 border">Amount</th>
              <th className="px-4 py-2 border">Balance</th>
              <th className="px-4 py-2 border">Days</th>
              <th className="px-4 py-2 border">Current</th>
              <th className="px-4 py-2 border">31-60</th>
              <th className="px-4 py-2 border">61-90</th>
              <th className="px-4 py-2 border">Over 90</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{row.invoice_number}</td>
                <td className="px-4 py-2 border">{row.date}</td>
                <td className="px-4 py-2 border">{row.reference}</td>
                <td className="px-4 py-2 border text-right">{toSafeFixed(row.amount, 2)}</td>
                <td className="px-4 py-2 border text-right">{toSafeFixed(row.balance, 2)}</td>
                <td className="px-4 py-2 border text-center">{row.days}</td>
                <td className="px-4 py-2 border text-right">{toSafeFixed(row.current, 2)}</td>
                <td className="px-4 py-2 border text-right">{toSafeFixed(row.days_31_60, 2)}</td>
                <td className="px-4 py-2 border text-right">{toSafeFixed(row.days_61_90, 2)}</td>
                <td className="px-4 py-2 border text-right">{toSafeFixed(row.over_90, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ARAgingPage;
